import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, HelpCircle, Image as ImgIcon, Play, Scale, MessageSquare, ArrowRight, ChevronDown } from "lucide-react";
import PageBlocks from "@/components/site/PageBlocks";
import PageHeader from "@/components/site/PageHeader";
import LegalHelpBlock from "@/components/site/LegalHelpBlock";
import { SafeHTML } from "@/components/site/RichTextEditor";
import MediaEmbed from "@/components/site/MediaEmbed";

const ICONS = { article: BookOpen, faq: HelpCircle, infographie: ImgIcon, video: Play };
const COLORS = { article: "bg-cef-blue text-white", faq: "bg-cef-yellow", infographie: "bg-cef-pink text-white", video: "bg-cef-green" };

const TABS = [
  { id: "reformes", label: "Réformes & analyses", Icon: BookOpen },
  { id: "droits", label: "Tes droits (FAQ)", Icon: Scale },
];

export default function Comprendre() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "reformes";
  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <PageHeader
        slug="comprendre"
        fallbackTitle="Comprendre [l'école](yellow)"
        fallbackSubtitle="Articles, FAQ, infographies et vidéos pour mieux comprendre l'école et tes droits d'élève."
      />

      <PageBlocks slug="comprendre" className="mt-6" />

      <div className="mt-6 flex flex-wrap gap-2" data-testid="comprendre-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} className={`chip inline-flex items-center gap-1.5 ${tab === id ? "bg-black text-white" : "bg-white"}`} data-testid={`comprendre-tab-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "reformes" && <ReformesTab />}
        {tab === "droits" && <DroitsTab />}
      </div>
    </div>
  );
}

function ReformesTab() {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  React.useEffect(() => {
    const params = { section: "reformes" };
    if (filter) params.format_filter = filter;
    api.get("/pedago", { params }).then((r) => setItems(r.data));
  }, [filter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="reformes-filters">
        <button onClick={() => setFilter(null)} className={`chip ${!filter ? "bg-black text-white" : "bg-white"}`} data-testid="pedago-all">Tous</button>
        {["article", "faq", "infographie", "video"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`chip ${filter === f ? COLORS[f] : "bg-white"}`} data-testid={`pedago-${f}`}>{f}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pedago-grid">
        {items.map((p) => {
          const Icon = ICONS[p.format] || BookOpen;
          return (
            <Link key={p.id} to={`/comprendre/${p.id}`} className="neo-card p-5 bg-white block group hover:-translate-y-0.5 transition" data-testid={`pedago-card-${p.id}`}>
              <div className={`inline-flex items-center gap-2 chip ${COLORS[p.format]}`}><Icon className="w-3.5 h-3.5" /> {p.format}</div>
              <h3 className="mt-3 font-display font-black text-lg tracking-tight group-hover:underline">{p.title}</h3>
              <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.summary}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-cef-blue">
                Lire l'analyse complète <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          );
        })}
        {items.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500">Aucune ressource pour ce filtre.</div>}
      </div>
    </div>
  );
}

function DroitsTab() {
  const [legalHelp, setLegalHelp] = React.useState(null);
  const [droitsArticles, setDroitsArticles] = React.useState([]);
  React.useEffect(() => {
    api.get("/site-config").then((r) => setLegalHelp(r.data?.legal_help)).catch(() => {});
    api.get("/pedago", { params: { section: "droits" } }).then((r) => setDroitsArticles(r.data)).catch(() => {});
  }, []);
  return (
    <div className="grid gap-6" data-testid="droits-tab">
      {droitsArticles.length > 0 && (
        <div>
          <h2 className="font-display font-black text-xl tracking-tighter uppercase mb-3 flex items-center gap-2"><Scale className="w-5 h-5" /> Articles juridiques</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="droits-articles-grid">
            {droitsArticles.map((p) => {
              const Icon = ICONS[p.format] || BookOpen;
              return (
                <Link key={p.id} to={`/comprendre/${p.id}`} className="neo-card p-5 bg-white block group hover:-translate-y-0.5 transition" data-testid={`droits-card-${p.id}`}>
                  <div className={`inline-flex items-center gap-2 chip ${COLORS[p.format]}`}><Icon className="w-3.5 h-3.5" /> {p.format}</div>
                  <h3 className="mt-3 font-display font-black text-lg tracking-tight group-hover:underline">{p.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.summary}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-cef-blue">Lire <ArrowRight className="w-4 h-4" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <FAQList />

      {legalHelp && <LegalHelpBlock data={legalHelp} />}

      <div className="neo-card p-6 bg-cef-pink text-white" data-testid="urgence-card">
        <div className="font-display font-black text-xl uppercase">Tu es en difficulté ?</div>
        <p className="mt-2 text-sm">Centre PMS de ton école · <a href="https://www.sdj.be/" target="_blank" rel="noreferrer" className="underline font-bold">Service Droit des Jeunes (SDJ)</a> · <a href="https://inforjeunes.be/" target="_blank" rel="noreferrer" className="underline font-bold">Fédération InforJeunes</a> — accompagnement juridique gratuit et confidentiel</p>
      </div>
    </div>
  );
}

function FAQList() {
  const [faqs, setFaqs] = React.useState([]);
  const [themes, setThemes] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [activeTheme, setActiveTheme] = React.useState(null); // null = tous

  React.useEffect(() => {
    api.get("/faq").then((r) => setFaqs(r.data));
    api.get("/site-config").then((r) => setThemes(r.data?.categories?.faq_themes || []));
  }, []);

  // Search filters all themes; theme filter is additive
  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return faqs.filter((f) => {
      if (activeTheme && (f.category || "Sans thème") !== activeTheme) return false;
      if (!q) return true;
      return (
        (f.question || "").toLowerCase().includes(q) ||
        (f.answer || "").toLowerCase().includes(q) ||
        (f.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [faqs, search, activeTheme]);

  // Group by theme (category) — preserves theme order from site-config, "Sans thème" at end
  const grouped = React.useMemo(() => {
    const map = new Map();
    themes.forEach((t) => map.set(t, []));
    visible.forEach((f) => {
      const key = f.category || "Sans thème";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    });
    // Drop empty themes
    return [...map.entries()].filter(([, arr]) => arr.length > 0);
  }, [visible, themes]);

  return (
    <div data-testid="faq-list">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="font-display font-black text-2xl tracking-tighter uppercase flex items-center gap-2">
          <MessageSquare className="w-6 h-6" strokeWidth={2.5} /> FAQ vérifiée
        </div>
        <input
          className="neo-input max-w-xs"
          placeholder="Rechercher une question…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="faq-search"
        />
      </div>

      {themes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="faq-theme-filters">
          <button
            onClick={() => setActiveTheme(null)}
            className={`chip ${!activeTheme ? "bg-black text-white" : "bg-white"}`}
            data-testid="faq-theme-all"
          >
            Tous les thèmes
          </button>
          {themes.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTheme(t)}
              className={`chip ${activeTheme === t ? "bg-cef-blue text-white" : "bg-white"}`}
              data-testid={`faq-theme-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {grouped.length === 0 && (
        <div className="text-sm text-neutral-500">Aucune question ne correspond à ta recherche.</div>
      )}

      <div className="grid gap-6">
        {grouped.map(([theme, list]) => (
          <section key={theme} data-testid={`faq-theme-section-${theme}`}>
            <h3 className="font-display font-black text-lg tracking-tight uppercase mb-3 inline-flex items-center gap-2">
              <span className="bg-cef-yellow border-2 border-black px-2 -rotate-1 inline-block">{theme}</span>
              <span className="text-xs text-neutral-500 font-normal normal-case">· {list.length} question{list.length > 1 ? "s" : ""}</span>
            </h3>
            <div className="grid gap-2">
              {list.map((f) => (
                <details key={f.id} className="neo-card p-0 bg-white overflow-hidden group" data-testid={`faq-${f.id}`}>
                  <summary className="cursor-pointer flex items-start gap-3 p-4 list-none [&::-webkit-details-marker]:hidden hover:bg-cef-paper/40 transition">
                    {f.pinned && <span className="chip bg-cef-pink text-white text-[10px] py-0.5 shrink-0">épinglé</span>}
                    <span className="font-bold text-sm flex-1">{f.question}</span>
                    <ChevronDown className="w-5 h-5 shrink-0 transition group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <div className="px-4 pb-4 pt-2 border-t-2 border-black/10">
                    {/* Back-compat: old plain-text answers are wrapped in <p> with preserved newlines.
                        New answers are HTML produced by TipTap and sanitized via DOMPurify in SafeHTML. */}
                    {/<\w+/.test(f.answer || "") ? (
                      <SafeHTML html={f.answer || ""} className="text-sm leading-relaxed text-neutral-700" />
                    ) : (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap text-neutral-700">{f.answer}</p>
                    )}
                    {f.media_url && <MediaEmbed url={f.media_url} />}
                    {f.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {f.tags.map((t) => <span key={t} className="chip bg-cef-paper text-[10px] py-0.5">#{t}</span>)}
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
